import { useAppDispatch, useAppSelector } from "../store/index";
import { useCallback, useEffect, useState, useRef } from "react";
import {
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination,
  Box,
  CircularProgress,
} from "@mui/material";
import { setLastError, viewDetoxSession, editDetoxSession, Views } from "../store/session";
import { TableLabel } from "../components/tables";
import SettingsIcon from "@mui/icons-material/Settings";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { MainButton } from "../components/buttons";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  LIST_DETOX_SESSIONS,
  SOFT_DELETE_DETOX_SESSION,
} from "../Queries/DetoxQueries";
import { DigitalDetoxSession } from "../model/detox";
import { DetoxStatus, formatDateDisplay, buildDetoxSessionsWhereClause } from "../utils/detox";
import ReactTimeAgo from "react-time-ago";
import dayjs from "dayjs";

// NOTE: Use CommonJS entrypoint to avoid webpack "fully specified" ESM resolution issues under CRA.
const { DatePicker } = require("@mui/x-date-pickers/DatePicker") as typeof import("@mui/x-date-pickers/DatePicker");

const PAGE_SIZE = 50;

export const DigitalDetox = () => {
  const dispatch = useAppDispatch();
  const currentView = useAppSelector((state) => state.session.view);
  const [sessions, setSessions] = useState<DigitalDetoxSession[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [deletingSessionId, setDeletingSessionId] = useState<string | null>(null);
  
  // Sorting
  const [sortField, setSortField] = useState<'start_date' | 'end_date'>('start_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<DetoxStatus | 'all'>('all');
  const [dateFilter, setDateFilter] = useState<string>('');
  const [startDateFilter, setStartDateFilter] = useState<string>('');
  const [endDateFilter, setEndDateFilter] = useState<string>('');

  const [getSessions, { loading }] = useLazyQuery(LIST_DETOX_SESSIONS, {
    onCompleted: (data) => {
      if (data?.digital_detox_sessions) {
        // Use the stored status from database, don't compute
        setSessions(data.digital_detox_sessions);
        
        // Calculate total pages from aggregate count
        const totalCount = data.digital_detox_sessions_aggregate?.aggregate?.count || 0;
        setTotalPages(Math.ceil(totalCount / PAGE_SIZE));
      }
    },
    onError: (error) => {
      dispatch(setLastError(error.message));
    },
  });

  const [softDeleteSession] = useMutation(SOFT_DELETE_DETOX_SESSION, {
    onCompleted: () => {
      // Clear deleting state
      setDeletingSessionId(null);
      // Force refetch to get updated list after deletion
      loadSessions();
    },
    onError: (error) => {
      // Clear deleting state on error
      setDeletingSessionId(null);
      dispatch(setLastError(error.message));
    },
  });


  const loadSessions = useCallback(() => {
      dispatch(setLastError());
      
      // Build dynamic where clause using utility function
      const whereClause = buildDetoxSessionsWhereClause(
        statusFilter,
        dateFilter,
        startDateFilter,
        endDateFilter
      );

      // Build order_by clause
      const orderBy = [{ [sortField]: sortDirection }];

      const variables = {
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        where: whereClause,
        order_by: orderBy,
      };

      getSessions({ 
        variables,
        fetchPolicy: 'network-only' // Always fetch fresh data
      });

  }, [page, statusFilter, dateFilter, startDateFilter, endDateFilter, sortField, sortDirection, getSessions, dispatch]);

  // Load sessions when filters or page change
  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Refresh when view changes to DigitalDetox (e.g., navigating from sidebar)
  const prevViewRef = useRef<Views | null>(null);
  useEffect(() => {
    // Only reload if view just changed TO DigitalDetox (not if it was already DigitalDetox)
    if (currentView === Views.DigitalDetox && prevViewRef.current !== Views.DigitalDetox) {
      loadSessions();
    }
    prevViewRef.current = currentView;
  }, [currentView]);

  const handleManage = useCallback((session: DigitalDetoxSession) => {
    dispatch(viewDetoxSession(session));
  }, [dispatch]);

  const handleDelete = useCallback((session: DigitalDetoxSession) => {
    if (window.confirm('Are you sure you want to delete this session?')) {
      setDeletingSessionId(session.id);
      softDeleteSession({
        variables: {
          id: session.id,
        },
      });
    }
  }, [softDeleteSession]);

  const handleCreateNew = useCallback(() => {
    dispatch(editDetoxSession(undefined));
  }, [dispatch]);

  const handlePageChange = useCallback((event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  }, []);

  const handleFilterChange = useCallback(() => {
    setPage(1); // Reset to first page when filters change
  }, []);

  const handleSort = useCallback((field: 'start_date' | 'end_date') => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with default descending order
      setSortField(field);
      setSortDirection('desc');
    }
    setPage(1); // Reset to first page when sorting changes
  }, [sortField, sortDirection]);

  return (
    <Stack spacing={2}>
      <Stack direction={"row"} alignItems="center" justifyContent="space-between">
        <Typography variant="h4">Digital Detox Sessions</Typography>
        <MainButton onClick={handleCreateNew}>Create New Session</MainButton>
      </Stack>

      {/* Filters */}
      <Paper>
        <Stack spacing={2} padding={2} direction="row" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => {
                setStatusFilter(e.target.value as DetoxStatus | 'all');
                handleFilterChange();
              }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="scheduled">Scheduled</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>

          <DatePicker
            label="Single Date"
            value={dateFilter ? dayjs(dateFilter) : null}
            onChange={(newValue) => {
              const newDate = newValue ? dayjs(newValue).format("YYYY-MM-DD") : "";
              setDateFilter(newDate);
              // Clear start/end date filters when single date is selected
              if (newDate) {
                setStartDateFilter('');
                setEndDateFilter('');
              }
              handleFilterChange();
            }}
            slotProps={{
              field: { clearable: true },
              textField: {
                size: "small",
                InputLabelProps: { shrink: true },
                sx: { width: 180 },
              },
            }}
          />

          <DatePicker
            label="Start Date"
            value={startDateFilter ? dayjs(startDateFilter) : null}
            onChange={(newValue) => {
              const newDate = newValue ? dayjs(newValue).format("YYYY-MM-DD") : "";
              setStartDateFilter(newDate);
              // Clear single date filter when start/end date is selected
              if (newDate || endDateFilter) {
                setDateFilter('');
              }
              handleFilterChange();
            }}
            slotProps={{
              field: { clearable: true },
              textField: {
                size: "small",
                InputLabelProps: { shrink: true },
                sx: { width: 180 },
              },
            }}
          />

          <DatePicker
            label="End Date"
            value={endDateFilter ? dayjs(endDateFilter) : null}
            onChange={(newValue) => {
              const newDate = newValue ? dayjs(newValue).format("YYYY-MM-DD") : "";
              setEndDateFilter(newDate);
              // Clear single date filter when start/end date is selected
              if (newDate || startDateFilter) {
                setDateFilter('');
              }
              handleFilterChange();
            }}
            slotProps={{
              field: { clearable: true },
              textField: {
                size: "small",
                InputLabelProps: { shrink: true },
                sx: { width: 180 },
              },
            }}
          />

          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setStatusFilter('all');
              setDateFilter('');
              setStartDateFilter('');
              setEndDateFilter('');
              handleFilterChange();
            }}
          >
            Clear Filters
          </Button>
        </Stack>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <Button
                  onClick={() => handleSort('start_date')}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 'bold',
                    color: 'inherit',
                    padding: 0,
                    minWidth: 'auto',
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                  }}
                  endIcon={
                    sortField === 'start_date' ? (
                      sortDirection === 'asc' ? (
                        <ArrowUpwardIcon fontSize="small" />
                      ) : (
                        <ArrowDownwardIcon fontSize="small" />
                      )
                    ) : null
                  }
                >
                  Start Date
                </Button>
              </TableCell>
              <TableCell>
                <Button
                  onClick={() => handleSort('end_date')}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 'bold',
                    color: 'inherit',
                    padding: 0,
                    minWidth: 'auto',
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                  }}
                  endIcon={
                    sortField === 'end_date' ? (
                      sortDirection === 'asc' ? (
                        <ArrowUpwardIcon fontSize="small" />
                      ) : (
                        <ArrowDownwardIcon fontSize="small" />
                      )
                    ) : null
                  }
                >
                  End Date
                </Button>
              </TableCell>
              <TableLabel label="Status" />
              <TableLabel label="Events" />
              <TableLabel label="Updated" />
              <TableLabel label="Actions" />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography>Loading...</Typography>
                </TableCell>
              </TableRow>
            ) : sessions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography>No sessions found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              sessions.map((session) => {
                const eventsCount = session.detox_events_aggregate?.aggregate?.count || 0;
                // Use the stored status from database, don't compute
                const status = session.status || 'scheduled';
                
                return (
                  <TableRow
                    key={session.id}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell>
                      <Typography>{formatDateDisplay(session.start_date)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>{formatDateDisplay(session.end_date)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          textTransform: 'capitalize',
                          color: status === 'active' ? 'success.main' : 
                                 status === 'cancelled' ? 'error.main' :
                                 status === 'completed' ? 'text.secondary' : 'primary.main'
                        }}
                      >
                        {status}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>{eventsCount}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontSize="0.9em">
                        <ReactTimeAgo date={new Date(session.updated_at)} />
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction={"row"} spacing={1}>
                        <Button
                          onClick={() => handleManage(session)}
                          color="primary"
                          variant="contained"
                          size="small"
                          startIcon={<SettingsIcon />}
                        >
                          Manage
                        </Button>
                        <Button
                          onClick={() => handleDelete(session)}
                          color="error"
                          variant="outlined"
                          size="small"
                          disabled={deletingSessionId === session.id}
                          startIcon={
                            deletingSessionId === session.id ? (
                              <CircularProgress size={16} color="error" />
                            ) : (
                              <DeleteIcon />
                            )
                          }
                        >
                          Delete
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center">
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}
    </Stack>
  );
};
